// Settings → Holiday Management.
//
// Mirrors `tricivo_system/operations/manage_holidays.php` exactly:
//  - Stat cards (Total · Upcoming · Ongoing · Completed)
//  - Bank-holiday import banner + 3-step wizard modal
//  - Inline "Schedule New Holiday" gradient form
//  - Status filter strip (All · Upcoming · Ongoing · Completed · Cancelled)
//  - Holidays table with cancel / reinstate / delete actions

import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  CalendarCheck2,
  CalendarPlus,
  CalendarRange,
  CalendarX,
  CheckCircle2,
  Clock,
  Flag,
  RotateCcw,
  Sun,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge, type BadgeVariant } from '../../../components/ui/Badge/Badge';
import { Table, type Column, type RowAction } from '../../../components/ui/Table/Table';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import {
  useHolidays,
  useUpdateHoliday,
  useDeleteHoliday,
} from './hooks/useHolidays';
import { ScheduleHolidayForm } from './components/ScheduleHolidayForm';
import { BankHolidayWizard } from './components/BankHolidayWizard';
import type {
  HolidayCurrentStatus,
  HolidayListItem,
} from '../../../api/holidays';
import { useAuthStore } from '../../../store/authStore';
import { periodLabel } from '../../../lib/holidayPeriod';
import styles from './HolidaysPage.module.css';

type FilterKey = 'all' | HolidayCurrentStatus;

const FILTER_LABELS: Record<FilterKey, string> = {
  all: 'All Holidays',
  upcoming: 'Upcoming',
  ongoing: 'Ongoing',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const STATUS_BADGE_VARIANTS: Record<HolidayCurrentStatus, BadgeVariant> = {
  upcoming: 'warning',
  ongoing: 'info',
  completed: 'success',
  cancelled: 'danger',
};

const STATUS_ICONS: Record<HolidayCurrentStatus, JSX.Element> = {
  upcoming: <Clock size={12} aria-hidden />,
  ongoing: <Sun size={12} aria-hidden />,
  completed: <CheckCircle2 size={12} aria-hidden />,
  cancelled: <XCircle size={12} aria-hidden />,
};

function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatCreatedAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function HolidaysPage(): JSX.Element {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const [filter, setFilter] = useState<FilterKey>('all');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<HolidayListItem | null>(null);
  const [reinstateTarget, setReinstateTarget] = useState<HolidayListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HolidayListItem | null>(null);

  const list = useHolidays({ page: 1, limit: 100 });
  const updateMut = useUpdateHoliday();
  const deleteMut = useDeleteHoliday();

  const all = list.data?.holidays ?? [];
  const counts = list.data?.counts ?? {
    total: 0,
    upcoming: 0,
    ongoing: 0,
    completed: 0,
    cancelled: 0,
  };

  const visibleRows = useMemo(() => {
    if (filter === 'all') return all;
    return all.filter((h) => h.current_status === filter);
  }, [all, filter]);

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <PageHeader title="Holiday Management" />
        <Card>
          <EmptyState
            title="Admin access required"
            description="Only company admins can manage holidays."
          />
        </Card>
      </div>
    );
  }

  const subtitle = `Manage holiday schedules for your team. ${counts.upcoming} upcoming, ${counts.ongoing} ongoing, ${counts.completed} completed holidays.`;

  const columns: Column<HolidayListItem>[] = [
    {
      key: 'entity_name',
      header: 'Employee',
      render: (_v, row) => <strong>{row.entity_name}</strong>,
    },
    {
      key: 'entity_type',
      header: 'Type',
      render: (_v, row) => (
        <Badge variant="info">
          {row.entity_type === 'staff' ? 'Staff' : 'Driver'}
        </Badge>
      ),
    },
    {
      key: 'holiday_start',
      header: 'Holiday Period',
      render: (_v, row) => (
        <span>
          <strong>{formatDate(row.holiday_start)}</strong> to{' '}
          <strong>{formatDate(row.holiday_end)}</strong>
          {row.period !== 'FULL' ? (
            <>
              {' '}
              <Badge variant="warning">{periodLabel(row.period)}</Badge>
            </>
          ) : null}
        </span>
      ),
    },
    {
      key: 'total_days',
      header: 'Days',
      align: 'right',
      render: (_v, row) => (
        <span>
          <strong>{row.total_days}</strong> days
        </span>
      ),
    },
    {
      key: 'current_status',
      header: 'Status',
      render: (_v, row) => (
        <Badge variant={STATUS_BADGE_VARIANTS[row.current_status]} icon={STATUS_ICONS[row.current_status]}>
          {row.current_status.charAt(0).toUpperCase() + row.current_status.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'notes',
      header: 'Notes',
      render: (_v, row) => row.notes ?? '-',
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (_v, row) => formatCreatedAt(row.created_at),
    },
  ];

  const rowActions = (row: HolidayListItem): RowAction<HolidayListItem>[] => {
    const actions: RowAction<HolidayListItem>[] = [];
    if (row.status === 'cancelled') {
      actions.push({
        label: 'Reinstate',
        icon: RotateCcw,
        variant: 'primary',
        onClick: () => setReinstateTarget(row),
      });
    } else if (
      row.status === 'active' &&
      (row.current_status === 'upcoming' || row.current_status === 'ongoing')
    ) {
      actions.push({
        label: 'Cancel',
        icon: CalendarX,
        variant: 'secondary',
        onClick: () => setCancelTarget(row),
      });
    }
    actions.push({
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: () => setDeleteTarget(row),
    });
    return actions;
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="Holiday Management"
        subtitle={subtitle}
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/settings')}>
            Back
          </Button>
        }
      />

      {/* Stat cards */}
      <ul className={styles.stats} aria-label="Holiday statistics">
        <li className={styles.stat}>
          <span className={styles.statIcon} style={{ color: 'var(--color-success, #10b981)' }} aria-hidden>
            <CalendarPlus size={20} />
          </span>
          <span className={styles.statBody}>
            <span className={styles.statCount}>{counts.total}</span>
            <span className={styles.statLabel}>Total Holidays</span>
          </span>
        </li>
        <li className={styles.stat}>
          <span className={styles.statIcon} style={{ color: 'var(--color-secondary, #2563eb)' }} aria-hidden>
            <Clock size={20} />
          </span>
          <span className={styles.statBody}>
            <span className={styles.statCount}>{counts.upcoming}</span>
            <span className={styles.statLabel}>Upcoming</span>
          </span>
        </li>
        <li className={styles.stat}>
          <span className={styles.statIcon} style={{ color: '#8b5cf6' }} aria-hidden>
            <Sun size={20} />
          </span>
          <span className={styles.statBody}>
            <span className={styles.statCount}>{counts.ongoing}</span>
            <span className={styles.statLabel}>Ongoing</span>
          </span>
        </li>
        <li className={styles.stat}>
          <span className={styles.statIcon} style={{ color: 'var(--color-text-muted, #94a3b8)' }} aria-hidden>
            <CalendarCheck2 size={20} />
          </span>
          <span className={styles.statBody}>
            <span className={styles.statCount}>{counts.completed}</span>
            <span className={styles.statLabel}>Completed</span>
          </span>
        </li>
      </ul>

      {/* Bank-holiday import banner */}
      <div className={styles.importBanner}>
        <span className={styles.bannerIcon} aria-hidden>
          <Flag size={18} />
        </span>
        <span className={styles.bannerText}>
          <strong>Import UK Bank Holidays</strong>
          <span>
            Instantly assign England &amp; Wales bank holidays for {new Date().getFullYear()} to
            your entire team in one go.
          </span>
        </span>
        <Button variant="secondary" icon={CalendarRange} onClick={() => setWizardOpen(true)}>
          Import Bank Holidays
        </Button>
      </div>

      {/* Schedule form */}
      <ScheduleHolidayForm />

      {/* Status filter strip */}
      <div className={styles.filters} role="tablist" aria-label="Filter holidays by status">
        {(Object.keys(FILTER_LABELS) as FilterKey[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={filter === key}
            className={[styles.filter, filter === key ? styles.filterActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => setFilter(key)}
          >
            {FILTER_LABELS[key]}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card flush>
        <Table
          columns={columns}
          data={visibleRows}
          keyField={(row) => row.uuid}
          loading={list.isLoading}
          rowActions={rowActions}
          ariaLabel="Holidays"
          emptyState={
            <EmptyState
              icon={CalendarPlus}
              title="No Holidays Scheduled"
              description="Schedule holidays for your team members using the form above, or import UK bank holidays with the button at the top."
            />
          }
        />
      </Card>

      {/* Wizard */}
      <BankHolidayWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />

      {/* Cancel confirm */}
      <ConfirmModal
        open={cancelTarget !== null}
        title="Cancel this holiday?"
        message={
          cancelTarget !== null
            ? `Are you sure you want to cancel ${cancelTarget.entity_name}'s holiday from ${formatDate(cancelTarget.holiday_start)} to ${formatDate(cancelTarget.holiday_end)}?`
            : ''
        }
        confirmLabel="Yes, Cancel Holiday"
        cancelLabel="Keep Holiday"
        variant="danger"
        loading={updateMut.isPending}
        onCancel={() => setCancelTarget(null)}
        onConfirm={() => {
          if (cancelTarget === null) return;
          updateMut.mutate(
            { uuid: cancelTarget.uuid, action: 'cancel' },
            { onSuccess: () => setCancelTarget(null) },
          );
        }}
      />

      {/* Reinstate confirm */}
      <ConfirmModal
        open={reinstateTarget !== null}
        title="Reinstate this holiday?"
        message={
          reinstateTarget !== null
            ? `Reinstate ${reinstateTarget.entity_name}'s holiday from ${formatDate(reinstateTarget.holiday_start)} to ${formatDate(reinstateTarget.holiday_end)}?`
            : ''
        }
        confirmLabel="Yes, Reinstate"
        cancelLabel="Keep Cancelled"
        loading={updateMut.isPending}
        onCancel={() => setReinstateTarget(null)}
        onConfirm={() => {
          if (reinstateTarget === null) return;
          updateMut.mutate(
            { uuid: reinstateTarget.uuid, action: 'reinstate' },
            { onSuccess: () => setReinstateTarget(null) },
          );
        }}
      />

      {/* Delete confirm */}
      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete holiday record?"
        message="This will permanently delete this holiday record. This cannot be undone."
        confirmLabel="Yes, Delete Permanently"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget === null) return;
          deleteMut.mutate(deleteTarget.uuid, {
            onSuccess: () => setDeleteTarget(null),
          });
        }}
      />
    </div>
  );
}
